import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { base64UrlDecode } from '../utils/common_util';

// AuthContext 생성
const AuthContext = createContext(null);

// AuthProvider 컴포넌트
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);  // 인증 상태
  const [loading, setLoading] = useState(true);  // 로딩 상태
  const [user, setUser] = useState(null); // 사용자 정보 저장
  const [tokenExpirationTime, setTokenExpirationTime] = useState(null); // 토큰 만료 시간
  const [timeRemaining, setTimeRemaining] = useState(null); // 남은 시간 (초)

  // JWT 토큰 유효성 검증 함수
  const isTokenValid = useCallback((token) => {
    if (!token || token.trim().length === 0) {
      return false;
    }

    try {
      
      // JWT 토큰은 3부분으로 나뉘어져 있음: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.log('Invalid token format');
        return false;
      }

      // payload 부분 디코딩 (UTF-8 지원)
      const decodedPayload = base64UrlDecode(parts[1]); // Base64 URL 디코딩
      const payload = JSON.parse(decodedPayload); // JSON 파싱
      
      // 만료 시간 확인 (exp는 Unix timestamp)
      if (payload.exp) {
        const expirationTime = payload.exp * 1000; // 밀리초로 변환
        const currentTime = Date.now();
        
        if (currentTime >= expirationTime) {
          console.log('Token expired! --> 만료시간(Exp):', new Date(expirationTime), '현재시간(Current):', new Date(currentTime));
          // 만료된 token 삭제
          localStorage.removeItem('token');
          return false;
        }
      }

      // 유효한 토큰
      return true;
    } catch (error) {
      console.error('Token validation error:', error);
      // 유효하지 않은 token 삭제
      localStorage.removeItem('token');
      return false;
    }
  }, []);

  // JWT 토큰에서 만료 시간 추출
  const getTokenExpirationTime = useCallback((token) => {
    if (!token) return null;
    
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      const decodedPayload = base64UrlDecode(parts[1]);
      const payload = JSON.parse(decodedPayload);
      
      if (payload.exp) {
        return payload.exp * 1000; // 밀리초로 변환
      }
      return null;
    } catch (error) {
      console.error('Failed to extract expiration time from token:', error);
      return null;
    }
  }, []);

  // JWT 토큰에서 사용자 정보 추출
  const getUserFromToken = useCallback((token) => {
    if (!token) return null;
    
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      // UTF-8 인코딩을 고려한 디코딩
      const decodedPayload = base64UrlDecode(parts[1]);
      const payload = JSON.parse(decodedPayload);
      
      console.log('JWT 토큰 payload 정보:', payload);
      
      // JWT 클레임: 백엔드 JwtTokenUtil — sub, userId, name, compCd, masterYn, exp
      const masterYn = payload.masterYn === 'Y' || payload.masterYn === true ? 'Y' : 'N';
      const userInfo = {
        id: payload.sub || payload.id || payload.userId || '',
        username: payload.username || payload.name || payload.sub || '사용자',
        name: payload.name || '',
        compCd: payload.compCd != null && payload.compCd !== '' ? String(payload.compCd) : '',
        masterYn,
        /** 시스템 전체 관리자 — 기관코드 화면 편집 허용 */
        isMaster: masterYn === 'Y',
      };

      console.log('User info:', userInfo);
      return userInfo;
    } catch (error) {
      console.error('Failed to extract user from token:', error);
      return null;
    }
  }, []);

  // token 확인 함수 (더 엄격한 검증)
  const checkToken = useCallback(() => {
    const token = localStorage.getItem('token');
    
    // token 유효성 검증 (형식, 만료 시간 등)
    const hasValidToken = isTokenValid(token);
    
    // 사용자 정보 추출
    const userInfo = hasValidToken ? getUserFromToken(token) : null;
    
    // 토큰 만료 시간 업데이트
    if (hasValidToken && token) {
      const expirationTime = getTokenExpirationTime(token);
      setTokenExpirationTime(expirationTime);
    } else {
      setTokenExpirationTime(null);
      setTimeRemaining(null);
    }
    
    // 현재 상태와 다를 때만 업데이트 (불필요한 리렌더링 방지)
    setIsAuthenticated( (prev) => {
      if (prev !== hasValidToken) { // 이전 상태와 현재 상태가 다를 때만 업데이트
        console.log('isAuthenticated changed:', prev, '->', hasValidToken);
        return hasValidToken;
      }
      return prev;
    });
    
    // 사용자 정보 업데이트
    setUser(userInfo);
    
    return hasValidToken;
  }, [isTokenValid, getUserFromToken, getTokenExpirationTime]);

  // 컴포넌트 마운트 시 localStorage에서 토큰 확인
  useEffect(() => {
    // 개발 환경에서 localStorage 클리어 옵션 (필요시 주석 해제)
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('Development mode: Clearing localStorage');
    //   localStorage.removeItem('token');
    // }
    
    checkToken();
    setLoading(false);

    // localStorage 변경 감지 (다른 탭이나 api.js에서 token 삭제 시)
    const handleStorageChange = (e) => {
      // token 키가 명시적으로 변경된 경우만 처리
      if (e.key === 'token') {
        // token이 삭제되었거나 변경된 경우
        checkToken();
      } else if (e.key === null) {
        // localStorage 전체가 클리어된 경우
        console.log('localStorage cleared, setting isAuthenticated to false');
        setIsAuthenticated(false);
      }
    };

    // storage 이벤트 리스너 (다른 탭에서 변경된 경우)
    window.addEventListener('storage', handleStorageChange);

    // 커스텀 이벤트 리스너 (같은 탭에서 변경된 경우)
    window.addEventListener('tokenChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tokenChanged', handleStorageChange);
    };
  }, [checkToken]);

  // 로그인 함수
  const login = (token) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    // 사용자 정보 추출 및 저장
    const userInfo = getUserFromToken(token);
    setUser(userInfo);
    // 토큰 만료 시간 저장
    const expirationTime = getTokenExpirationTime(token);
    setTokenExpirationTime(expirationTime);
    // 다른 컴포넌트에 token 변경 알림
    window.dispatchEvent(new Event('tokenChanged'));
  };

  // 로그아웃 함수
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
    setTokenExpirationTime(null);
    setTimeRemaining(null);
    // 다른 컴포넌트에 token 삭제 알림
    window.dispatchEvent(new Event('tokenChanged'));
  }, []);

  // 토큰 만료 체크 및 자동 로그아웃 (주기적으로 실행)
  useEffect(() => {
    if (!isAuthenticated || !tokenExpirationTime) {
      setTimeRemaining(null);
      return;
    }

    // 1초마다 남은 시간 업데이트
    const updateTimeRemaining = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((tokenExpirationTime - now) / 1000)); // 초 단위
      
      setTimeRemaining(remaining);

      // 만료되었으면 자동 로그아웃
      if (remaining <= 0) {
        console.log('토큰이 만료되어 자동 로그아웃합니다.');
        logout();
        // 로그인 페이지로 이동 (window.location 사용)
        window.location.href = '/login';
      }
    };

    // 즉시 한 번 실행
    updateTimeRemaining();

    // 1초마다 업데이트
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isAuthenticated, tokenExpirationTime, logout]);

  const value = {
    isAuthenticated,
    loading,
    user,
    /** 세션(토큰) 기준 기관코드 — 일반 사용자 화면에서 기관 분리에 사용 */
    compCd: user?.compCd ?? '',
    /** 마스터 권한(MASTER_YN=Y) — 기관코드 입력·변경 허용 */
    isMaster: user?.isMaster === true || user?.masterYn === 'Y',
    login,
    logout,
    timeRemaining, // 남은 시간 (초 단위)
  };
//value: 공유할 데이터와 함수를 객체로 묶음
//<AuthContext.Provider value={value}>: 이 Provider 안의 모든 컴포넌트가 value에 접근 가능
//{children}: Provider로 감싼 자식 컴포넌트들
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// useAuth Hook (컴포넌트에서 쉽게 사용하기 위한 커스텀 Hook)
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

